import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, MapPin, MessageCircle, Phone, Send } from "lucide-react";
import { HeroFade, FadeUp, StaggerGrid, StaggerItem } from "@/lib/motion";
import { useSEO } from "@/hooks/useSEO";

export default function Contact() {
  const { site } = useOutletContext();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useSEO({
    title: "Contact Us & Find Location",
    description: "Get in touch with Afzal Medical Complex Pakistan. Find our emergency contact numbers, maps embed location, telephone, email address, and book appointments."
  });

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! Your message has been sent. We will reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="section bg-red-50/40">
        <div className="container-medi max-w-3xl">
          <HeroFade><div className="overline mb-3">Contact</div></HeroFade>
          <HeroFade delay={0.1}><h1 className="text-4xl sm:text-5xl font-heading font-semibold tracking-tight text-slate-900">We&apos;re here to help</h1></HeroFade>
          <HeroFade delay={0.2}><p className="mt-5 text-slate-600 text-lg">Reach out for appointments, feedback or any questions. Our team will respond within 24 hours.</p></HeroFade>
        </div>
      </section>

      <section className="section">
        <div className="container-medi grid lg:grid-cols-5 gap-10">
          <StaggerGrid className="lg:col-span-2 space-y-4">
            {[
              { icon: MapPin, label: "Address", value: site?.address || "Main GT Road, Afzal Medical Complex" },
              { icon: Phone, label: "Phone", value: site?.phone || "0341-9300560", href: `tel:${site?.phone || "0341-9300560"}` },
              { icon: MessageCircle, label: "WhatsApp", value: site?.phone || "0341-9300560", href: `https://wa.me/${site?.whatsapp || "923419300560"}`, color: "emerald" },
              { icon: Mail, label: "Email", value: site?.email || "contact@afzalmedicalcomplex.com", href: `mailto:${site?.email || "contact@afzalmedicalcomplex.com"}` },
            ].map(({ icon: Ico, label, value, href, color }) => (
              <StaggerItem key={label} className="card-medi p-6 flex gap-4">
                <div className={`w-11 h-11 rounded-xl ${color === "emerald" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"} grid place-items-center flex-shrink-0`}><Ico className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-red-700 font-semibold">{label}</div>
                  {href ? <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-slate-800 hover:text-red-700 break-all">{value}</a> : <div className="text-slate-800">{value}</div>}
                </div>
              </StaggerItem>
            ))}
          </StaggerGrid>

          <FadeUp className="lg:col-span-3" delay={0.15}>
            <form onSubmit={submit} className="card-medi p-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your Name" name="name" value={form.name} onChange={onChange} required testid="contact-name" />
              <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required testid="contact-email" />
              <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid="contact-phone" />
              <Field label="Subject" name="subject" value={form.subject} onChange={onChange} testid="contact-subject" />
            </div>
            <div className="mt-4">
              <label className="text-sm font-medium text-slate-700">Message</label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                required
                rows={6}
                className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                data-testid="contact-message"
              />
            </div>
            <button type="submit" disabled={submitting} className="btn-primary mt-6 disabled:opacity-60" data-testid="contact-submit">
              <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
          </FadeUp>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-medi">
          <FadeUp>
          <div className="rounded-3xl overflow-hidden shadow-medi-md border border-slate-100">
            <iframe
              title="Map"
              src={site?.map_embed_url || "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13612.05!2d74.34!3d31.55!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1"}
              className="w-full h-[420px] border-0"
              loading="lazy"
              data-testid="contact-map"
            />
          </div>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}{required && <span className="text-red-600">*</span>}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
        data-testid={testid}
      />
    </div>
  );
}
